import React, { useEffect, useMemo, useState, useCallback, useLayoutEffect, useRef } from 'react';
import moment from 'moment';
import { cn } from 'App/shadcn/lib/utils';
import showdown from 'showdown';
// @ts-ignore - sanitize-html lacks type definitions
import sanitizeHtml from 'sanitize-html';
import ImageModal from './ImageModal';
import PdfPreviewModal from './PdfPreviewModal';
import T from 'Components/Helpers/translate';
import type { MessageAttachment } from './types';
import { attachmentKindFor } from './attachmentMeta';
import AttachmentChip from './AttachmentChip';
import { openDrawer } from 'Services/Drawer';
import openAgreement from 'App/helpers/openSubscriptionHelper';
import { openEditOrder } from 'App/components/EditOrder';
import { openEditAppointment } from 'Components/Modals/Appointment/EditAppointment';
import { shouldIgnoreRepeatOpen } from './entityOpenGuard';

const brandInitials = (name?: string): string => {
	const words = (name || '').trim().split(/\s+/).filter(Boolean);
	if (words.length === 0) return 'A';
	if (words.length === 1) return words[0].charAt(0).toUpperCase();
	return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

// Always show locale-aware long date + time so each bubble is self-dating.
// Routine threads (e.g. post-meeting) accumulate runs across days under a
// single conversation, where a time-only header can't tell you which day a
// run is from. `LL` renders the month name and keeps the year, localized
// (e.g. "June 1, 2026" / "1 juni 2026").
const formatMessageTimestamp = (date: Date): string => moment(date).format('LL LT');

const converter = new showdown.Converter({
	tables: true,
	simplifiedAutoLink: true,
	strikethrough: true,
	tasklists: true
});

const SANITIZE_CONFIG = {
	allowedTags: [
		'p',
		'div',
		'span',
		'br',
		'hr',
		'h1',
		'h2',
		'h3',
		'h4',
		'h5',
		'h6',
		'strong',
		'em',
		'b',
		'i',
		'u',
		's',
		'del',
		'ul',
		'ol',
		'li',
		'table',
		'thead',
		'tbody',
		'tr',
		'td',
		'th',
		'a',
		'blockquote',
		'pre',
		'code',
		'input',
		'img'
	],
	allowedAttributes: {
		a: ['href', 'target', 'rel'],
		input: ['type', 'checked', 'disabled'],
		code: ['class'],
		td: ['align'],
		th: ['align'],
		img: ['src', 'alt', 'title', 'width', 'height']
	},
	allowedSchemes: ['http', 'https', 'mailto', 'tel', 'upsales'],
	allowedSchemesByTag: {
		img: ['http', 'https']
	}
};

const PROSE_CLASSES = 'pr-prose';

const COPY_ICON =
	'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';

// Wrap fenced code blocks in an "editor window" with a chrome bar (window dots,
// language pill, copy button). Runs on already-sanitized HTML, so the injected
// markup is trusted. The <pre><code> body is preserved verbatim.
// Sentinel line the pages chat inserts between edit hunks so several scattered
// changes in one turn read as distinct blocks instead of one merged wall — the
// diff renderer swaps it for a faint divider row. Shared so producer and renderer
// can't drift (WEB-7756).
export const DIFF_HUNK_SEP = '⋯⋯⋯';

// Summary label for a collapsed diff block. An all-additions block (no removed
// lines) is a from-scratch write/rewrite → "written"; a mix is an edit → "changed".
const diffSummary = (added: number, removed: number): string =>
	removed === 0
		? T('assistant.code.linesWritten', { count: added })
		: T('assistant.code.linesChanged', { count: added + removed });

const wrapCodeBlocks = (
	src: string,
	copyLabel: string,
	diffOpen: boolean,
	changeSummary: (added: number, removed: number) => string
): string =>
	src.replace(
		/<pre><code(?:\s+class="([^"]*)")?>([\s\S]*?)<\/code><\/pre>/g,
		(_m: string, cls: string | undefined, code: string) => {
			const lang = (cls || '')
				.split(/\s+/)
				.map(c => c.replace(/^lang(uage)?-/, ''))
				.filter(Boolean)[0];
			// A ```diff block renders as a compact change view, collapsed behind a
			// summary by default so history stays scannable, and coloured by each
			// line's leading +/- (added / removed). It stays open while the turn is
			// still streaming so the code is visible as it's written (WEB-7757).
			// A ```diff-rewrite / ```diff-new block is the same view, but the producer
			// (pages chat) has flagged it as a full-file rewrite of existing source, or
			// a first build of a page, rather than a targeted edit — we surface a chip
			// so the user sees the moment the agent chose a whole-file block instead of
			// patching (WEB-7756). The flag is carried on the fence, not inferred from
			// the diff shape: an additive-only edit is also all-additions, and a rewrite
			// and a first build are both all-additions, so the shape can't tell them apart.
			if (lang === 'diff' || lang === 'diff-rewrite' || lang === 'diff-new') {
				const lines = code.replace(/\n$/, '').split('\n');
				let added = 0;
				let removed = 0;
				// New-file line numbers (WEB-7821): number context and added lines; a
				// removed line has no new-file line so its gutter stays blank. For a
				// full write/rewrite (all additions) this is simply 1..N. The gutter is
				// user-select:none so copying the block doesn't drag the numbers along.
				let lineNo = 0;
				const rows = lines
					.map(line => {
						if (line === DIFF_HUNK_SEP)
							return `<span class="pr-diff-line pr-diff-sep" aria-hidden="true"></span>`;
						const sign = line.charAt(0);
						if (sign === '+') added++;
						else if (sign === '-') removed++;
						const mod = sign === '-' ? ' pr-diff-del' : sign === '+' ? ' pr-diff-add' : '';
						const num = sign === '-' ? '' : String(++lineNo);
						return (
							`<span class="pr-diff-line${mod}">` +
							`<span class="pr-diff-num" aria-hidden="true">${num}</span>` +
							`<span class="pr-diff-text">${line || ' '}</span>` +
							`</span>`
						);
					})
					.join('');
				const rewriteChip =
					lang === 'diff-rewrite'
						? `<span class="pr-diff-tag">${T('assistant.pages.chat.fullRewrite')}</span>`
						: lang === 'diff-new'
						? `<span class="pr-diff-tag pr-diff-tag--new">${T('assistant.pages.chat.newPage')}</span>`
						: '';
				// Body is a <div>, NOT <pre>/<code>: inside the shadow DOM the diff
				// competes with `pre`/`code` rules from Tailwind preflight,
				// workspaceStyles and praxisStyles. A <div> is immune — only the
				// .pr-diff-* class rules style it (WEB-7757).
				return (
					`<details class="pr-diff"${diffOpen ? ' open' : ''}>` +
					`<summary class="pr-diff-summary">` +
					`<span class="pr-code-dots"><i></i><i></i><i></i></span>` +
					`<span class="pr-diff-caret"></span>` +
					rewriteChip +
					`<span class="pr-diff-title">${changeSummary(added, removed)}</span>` +
					`</summary>` +
					`<div class="pr-diff-body">${rows}</div>` +
					// Full-width drag handle at the card bottom (with a visible grip)
					// to resize the body height — see handleDiffResizeStart (WEB-7757).
					`<div class="pr-diff-resize" aria-hidden="true"><span class="pr-diff-grip"></span></div>` +
					`</details>`
				);
			}
			const safeLang = (lang || '').replace(/[^a-z0-9+#.-]/gi, '');
			const langTag = safeLang ? `<span class="pr-code-lang">${safeLang}</span>` : '';
			const codeTag = cls ? `<code class="${cls}">` : '<code>';
			return (
				`<figure class="pr-code"><figcaption class="pr-code-bar">` +
				`<span class="pr-code-dots"><i></i><i></i><i></i></span>${langTag}` +
				`<button type="button" class="pr-code-copy">${COPY_ICON}` +
				`<span class="pr-code-copy-text">${copyLabel}</span></button>` +
				`</figcaption><pre>${codeTag}${code}</code></pre></figure>`
			);
		}
	);

// Every entity type the click handler below has a `case` for. One list, used
// for both the href matcher and the chip-styling rewrite: they used to be two
// hand-maintained copies of the same alternation, and they drifted from the
// base-side LINK_TYPE_MAP (`upsales://lead/42` rendered as a link and did
// nothing when clicked — WEB-9210). Adding a type here without adding its
// `case` would swallow the click, so the two must be extended together.
const ROUTABLE_ENTITY_TYPES = [
	'client',
	'contact',
	'order',
	'appointment',
	'activity',
	'agreement',
	'todo',
	'phonecall',
	'opportunity',
	'ticket',
	'projectplan',
	'segment',
	'lead',
	'project',
	'agent',
	'task',
	'routine'
] as const;

const ENTITY_LINK_RE = new RegExp(`^upsales://(${ROUTABLE_ENTITY_TYPES.join('|')})/(\\d+)$`);

const getCustomerId = (): string => {
	const match = window.location.hash.match(/^#\/(\d+)\//);
	return match ? match[1] : '1';
};

const openEntityInNewTab = (stateName: string, id: number): boolean => {
	let href = Tools.$state?.href?.(stateName, { id, customerId: getCustomerId() });
	if (!href) return false;
	const hashIndex = href.indexOf('#');
	if (hashIndex !== -1) {
		const hashPart = href.substring(hashIndex);
		href = hashPart.startsWith('#/') ? `/${hashPart}` : `/#${hashPart.substring(1)}`;
	} else if (!href.startsWith('/')) {
		href = `/${href}`;
	}
	window.open(href, '_blank', 'noopener,noreferrer');
	return true;
};

type MentionAgent = { id: number; name: string };

type Props = {
	role: 'user' | 'assistant';
	content: string;
	streaming?: boolean;
	/**
	 * Whether a diff card is forced open while the turn is still streaming.
	 *
	 * True everywhere by default, which is the long-standing behaviour (WEB-7757):
	 * watching the code arrive IS the progress indicator. The Pages chat turns it
	 * off because it shows a counting-up progress row in its ledger instead, and
	 * a wall of TSX is a poor loading state for someone who is not going to read
	 * it (WEB-10047).
	 */
	openDiffWhileStreaming?: boolean;
	// Whether this message's diff block starts expanded. Owned by the parent so
	// the open state is sticky — it survives new messages and remembers the
	// user's manual toggle rather than auto-collapsing (WEB-7757).
	openDiff?: boolean;
	// Stable key for this message + a callback fired when the user toggles its
	// diff, so the parent can persist the open/closed state.
	diffKey?: number;
	onDiffToggle?: (key: number, open: boolean) => void;
	images?: string[];
	attachments?: MessageAttachment[];
	/**
	 * Rendered above a user message's bubble, in the same right-aligned column as
	 * the attachment chips (WEB-10047). Opaque here: what it draws is the caller's
	 * business, and a surface that passes nothing renders exactly as before.
	 */
	accessory?: React.ReactNode;
	avatarUrl?: string | null;
	avatarColor?: string;
	assistantName?: string;
	regDate?: string;
	agents?: MentionAgent[];
	onAgentClick?: (id: number) => void;
	onTaskClick?: (id: number) => void;
	onRoutineClick?: (id: number) => void;
	/** Optional element rendered inline in the header row, right of name/time. */
	headerAccessory?: React.ReactNode;
};

const MessageBlock = ({
	role,
	content,
	streaming = false,
	openDiff = false,
	openDiffWhileStreaming = true,
	diffKey,
	onDiffToggle,
	images,
	attachments,
	accessory,
	avatarUrl,
	avatarColor,
	assistantName,
	regDate,
	agents,
	onAgentClick,
	onTaskClick,
	onRoutineClick,
	headerAccessory
}: Props) => {
	const nonImageAttachments = useMemo(
		() => (attachments ?? []).filter(a => !(a.mimeType || '').toLowerCase().startsWith('image/')),
		[attachments]
	);
	const [previewPdf, setPreviewPdf] = useState<{ url: string; name: string } | null>(null);

	// Downloadable file chips (uploaded files on user messages; agent-generated
	// documents on assistant messages — WEB-7394). Rendered in both role branches.
	const renderAttachmentChips = (justifyClass = '') =>
		nonImageAttachments.length > 0 ? (
			<div className={cn('flex gap-2 mb-2 flex-wrap', justifyClass)}>
				{nonImageAttachments.map((att, i) => {
					const isPdf = attachmentKindFor(att.mimeType, att.name).className === 'pdf';
					const url = att.url;
					const Chip = <AttachmentChip name={att.name} mimeType={att.mimeType} sizeBytes={att.sizeBytes} />;
					if (!url) {
						return <div key={i}>{Chip}</div>;
					}
					// PDFs preview in-app on a plain click (so iterating doesn't mean
					// download-and-open each time); the href + target keeps modified/
					// middle clicks opening a new tab. Non-PDFs just open/download.
					return (
						<a
							key={i}
							href={url}
							target="_blank"
							rel="noopener noreferrer"
							className="pr-msg-attachment-link"
							onClick={
								isPdf
									? e => {
											if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
											e.preventDefault();
											setPreviewPdf({ url, name: att.name });
									  }
									: undefined
							}
						>
							{Chip}
						</a>
					);
				})}
			</div>
		) : null;
	const [modalImage, setModalImage] = useState<string | null>(null);
	const [avatarError, setAvatarError] = useState(false);

	const fileMatch = content.match(/\s*\[(\d+) file\(s\) attached\]$/);
	const displayContent = fileMatch ? content.replace(fileMatch[0], '').trim() : content;
	const attachmentCount = fileMatch ? parseInt(fileMatch[1], 10) : 0;

	const html = useMemo(() => sanitizeHtml(converter.makeHtml(displayContent), SANITIZE_CONFIG), [displayContent]);

	const styledHtml = useMemo(() => {
		if (!html) return html;
		try {
			let result = html.replace(
				new RegExp(`<a\\s+([^>]*?)href="(upsales://(?:${ROUTABLE_ENTITY_TYPES.join('|')})/\\d+)"([^>]*)>`, 'g'),
				(_match: string, before: string, href: string, after: string) =>
					`<a ${before}href="${href}"${after} class="pr-entity-chip">`
			);

			const replaceInTextNodes = (
				src: string,
				re: RegExp,
				replacer: (m: string, ...args: string[]) => string
			) => {
				return src.replace(/(<a\b[^>]*>.*?<\/a>)|(<[^>]+>)|([^<]+)/gs, (seg, anchor, tag, text) => {
					if (anchor || tag) return seg;
					return text.replace(re, replacer);
				});
			};

			result = replaceInTextNodes(
				result,
				/\(?\btask\s*#(\d+)\)?/gi,
				(m: string, id: string) =>
					`<a href="upsales://task/${id}" class="pr-entity-chip pr-mention-link">${m}</a>`
			);

			if (agents?.length) {
				const sorted = [...agents].sort((a, b) => b.name.length - a.name.length);
				for (const agent of sorted) {
					const escaped = agent.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
					// eslint-disable-next-line security/detect-non-literal-regexp -- `escaped` is regex-escaped on the line above
					const re = new RegExp(`\\b${escaped}\\b`, 'gi');
					result = replaceInTextNodes(
						result,
						re,
						(m: string) =>
							`<a href="upsales://agent/${agent.id}" class="pr-entity-chip pr-mention-link">${m}</a>`
					);
				}
			}

			// Render a colour swatch inline before any hex code (e.g. brand
			// colours the agent surfaces). Runs on text nodes only, so it never
			// touches hex inside href/style/existing markup. The style value is
			// the exact hex just matched — safe to inline.
			result = replaceInTextNodes(
				result,
				/#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{3})\b/g,
				(m: string) => `<span class="pr-color-swatch" style="background-color:${m}"></span>${m}`
			);

			return wrapCodeBlocks(
				result,
				T('default.copy'),
				(streaming && openDiffWhileStreaming) || openDiff,
				diffSummary
			);
		} catch {
			return wrapCodeBlocks(
				html,
				T('default.copy'),
				(streaming && openDiffWhileStreaming) || openDiff,
				diffSummary
			);
		}
	}, [html, agents, streaming, openDiff, openDiffWhileStreaming]);

	const bodyRef = useRef<HTMLDivElement>(null);
	useLayoutEffect(() => {
		if (!streaming) return;
		bodyRef.current?.querySelectorAll('.pr-code pre, .pr-diff-body').forEach(el => {
			el.scrollTop = el.scrollHeight;
		});
	}, [styledHtml, streaming]);

	// Report the user's manual expand/collapse of a diff so the parent can keep
	// the open state sticky (WEB-7757). The <details> lives in injected HTML, so
	// we listen for its native `toggle` after each render.
	useEffect(() => {
		const root = bodyRef.current;
		if (!root || !onDiffToggle || diffKey == null) return;
		const cards = Array.from(root.querySelectorAll<HTMLDetailsElement>('details.pr-diff'));
		const bound = cards.map(card => {
			const handler = () => onDiffToggle(diffKey, card.open);
			card.addEventListener('toggle', handler);
			return { card, handler };
		});
		return () => bound.forEach(({ card, handler }) => card.removeEventListener('toggle', handler));
	}, [styledHtml, onDiffToggle, diffKey]);

	// Drag the diff card's bottom handle to resize the body. The height is set
	// inline on the (unchanging) body element; React preserves it across renders
	// because it doesn't re-set dangerouslySetInnerHTML when the html is the same.
	const handleDiffResizeStart = useCallback((e: React.MouseEvent) => {
		const handle = (e.target as HTMLElement).closest('.pr-diff-resize');
		if (!handle) return;
		const body = handle.parentElement?.querySelector<HTMLElement>('.pr-diff-body');
		if (!body) return;
		e.preventDefault();
		const startY = e.clientY;
		const startH = body.getBoundingClientRect().height;
		const onMove = (ev: MouseEvent) => {
			const h = Math.max(80, Math.min(window.innerHeight * 0.85, startH + (ev.clientY - startY)));
			body.style.height = `${h}px`;
			body.style.maxHeight = 'none';
		};
		const onUp = () => {
			window.removeEventListener('mousemove', onMove);
			window.removeEventListener('mouseup', onUp);
			document.body.style.userSelect = '';
		};
		window.addEventListener('mousemove', onMove);
		window.addEventListener('mouseup', onUp);
		document.body.style.userSelect = 'none';
	}, []);

	const handleContentClick = useCallback(
		(e: React.MouseEvent) => {
			const target = e.target as HTMLElement;
			const copyBtn = target.closest('.pr-code-copy');
			if (copyBtn) {
				e.preventDefault();
				const fig = copyBtn.closest('.pr-code');
				const text = fig?.querySelector('pre')?.textContent ?? '';
				const labelEl = copyBtn.querySelector('.pr-code-copy-text');
				navigator.clipboard
					.writeText(text)
					.then(() => {
						if (!fig) return;
						fig.classList.add('is-copied');
						if (labelEl) labelEl.textContent = T('default.copiedToClipboard');
						window.setTimeout(() => {
							fig.classList.remove('is-copied');
							if (labelEl) labelEl.textContent = T('default.copy');
						}, 1600);
					})
					.catch(err => console.error('Failed to copy code block', err));
				return;
			}
			const img = target.closest('img');
			if (img && !target.closest('a')) {
				const src = img.getAttribute('src');
				if (src) {
					e.preventDefault();
					setModalImage(src);
					return;
				}
			}
			const anchor = target.closest('a');
			if (!anchor) return;
			const href = anchor.getAttribute('href');
			if (!href) return;
			const match = href.match(ENTITY_LINK_RE);
			if (!match) return;
			e.preventDefault();
			const entityType = match[1];
			const entityId = Number(match[2]);
			// A chip is as spammable as the cards' "Open" button — same guard, so
			// repeat clicks stop stacking modals and browser tabs.
			if (shouldIgnoreRepeatOpen(entityType, entityId)) return;
			switch (entityType) {
				case 'agent':
					onAgentClick?.(entityId);
					return;
				case 'task':
					onTaskClick?.(entityId);
					return;
				case 'routine':
					onRoutineClick?.(entityId);
					return;
				case 'client':
					openEntityInNewTab('account.dashboard', entityId);
					return;
				case 'contact':
					openEntityInNewTab('contact.dashboard', entityId);
					return;
				case 'order':
				case 'opportunity':
					openEditOrder({ id: entityId });
					return;
				case 'activity':
					Tools.$upModal.open('editActivity', { id: entityId });
					return;
				case 'appointment':
					openEditAppointment({ id: entityId });
					return;
				case 'todo':
					openDrawer('EditTodo', { todo: { id: entityId } });
					return;
				case 'phonecall':
					openDrawer('EditPhonecall', { activity: { id: entityId } });
					return;
				case 'agreement':
					openAgreement({ agreementId: entityId });
					return;
				case 'ticket':
					openDrawer('EditTicket', { ticketId: entityId });
					return;
				case 'projectplan':
					openDrawer('EditProjectPlan', { projectPlanId: entityId });
					return;
				case 'segment':
					openEntityInNewTab('segment', entityId);
					return;
				case 'lead':
					Tools.$upModal.open('editLead', { id: entityId, customerId: getCustomerId() });
					return;
				// `project` is the entity the UI calls Campaign / Kampanj.
				case 'project':
					openEntityInNewTab('campaign.dashboard', entityId);
					return;
			}
		},
		[onAgentClick, onTaskClick, onRoutineClick]
	);

	if (role === 'user') {
		const hasNonImage = nonImageAttachments.length > 0;
		const hasImages = images && images.length > 0;
		return (
			<>
				<div className="flex justify-end">
					<div className="max-w-[85%]">
						{hasImages && (
							<div className="flex gap-2 mb-2 justify-end flex-wrap">
								{images!.map((src, i) => (
									<img
										key={i}
										src={src}
										alt=""
										className="w-20 h-20 rounded-xl object-cover cursor-pointer hover:opacity-90 transition-opacity"
										onClick={() => setModalImage(src)}
									/>
								))}
							</div>
						)}
						{renderAttachmentChips('justify-end')}
						{accessory}
						{!hasImages && !hasNonImage && attachmentCount > 0 && (
							<div className="text-body-sm text-muted-foreground mb-1 text-right">
								{T('assistant.filesAttached', { count: attachmentCount })}
							</div>
						)}
						<div
							className="pr-user-bubble"
							onClick={handleContentClick}
							dangerouslySetInnerHTML={{ __html: styledHtml }}
						/>
					</div>
				</div>
				{modalImage && <ImageModal src={modalImage} onClose={() => setModalImage(null)} />}
				{previewPdf && (
					<PdfPreviewModal url={previewPdf.url} name={previewPdf.name} onClose={() => setPreviewPdf(null)} />
				)}
			</>
		);
	}

	return (
		<>
			<div className={cn('pr-asst-msg', !assistantName && 'pr-asst-msg--inline')}>
				<div className="pr-asst-head">
					<div
						className="pr-avatar-sm"
						style={!avatarUrl && avatarColor ? { background: avatarColor } : undefined}
					>
						{avatarUrl && !avatarError ? (
							<img src={avatarUrl} alt="" onError={() => setAvatarError(true)} />
						) : (
							brandInitials(assistantName)
						)}
					</div>
					{assistantName && (
						<div className="flex flex-1 items-center gap-2.5">
							<span className="pr-assistant-name">{assistantName}</span>
							<span className="pr-msg-time">
								{formatMessageTimestamp(regDate ? new Date(regDate) : new Date())}
							</span>
							{headerAccessory && <span className="ml-auto flex items-center">{headerAccessory}</span>}
						</div>
					)}
				</div>
				<div
					ref={bodyRef}
					className={cn(PROSE_CLASSES, streaming && 'pr-caret', 'pr-asst-body')}
					onClick={handleContentClick}
					onMouseDown={handleDiffResizeStart}
					dangerouslySetInnerHTML={{ __html: styledHtml }}
				/>
				{renderAttachmentChips()}
			</div>
			{modalImage && <ImageModal src={modalImage} onClose={() => setModalImage(null)} />}
			{previewPdf && (
				<PdfPreviewModal url={previewPdf.url} name={previewPdf.name} onClose={() => setPreviewPdf(null)} />
			)}
		</>
	);
};

export default MessageBlock;
